import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';
import { UserService } from '../../../services/user.service';
import { AccessAccountService } from '../../../services/access-account.service';
import { PostReqUser } from '../../../models/request/post-req-user.model';
import { GetResUserAccessAccount } from '../../../models/response/get-res-user-access-account.model';
import { PutReqUser } from '../../../models/request/put-req-user.model';
import { PutReqUserAccessAccount } from '../../../models/request/put-req-user-access-account.model';
import { Title } from '@angular/platform-browser';
import { AuthService } from '../../../services/auth.service';
interface permission {
  group: string;
  accessLevel: string;
}

@Component({
    selector: 'app-user-detail',
    templateUrl: './user-detail.component.html',
    styleUrl: './user-detail.component.css',
    standalone: false
})
export class UserDetailComponent implements OnInit {
  
  form: FormGroup;
  groups: {label: string, value: string}[] = [];
  profiles: {label: string, value: string}[] = [];
  permissions!: permission[];
  editMode: boolean = true;
  isEditMode: boolean = false;
  user: GetResUserAccessAccount = {} as GetResUserAccessAccount;
  selectedGroups: any[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private messageService: MessageService,
    private translocoService: TranslocoService,
    private userService: UserService,
    private accessAccountService: AccessAccountService,
    private titleService: Title,
    private authService: AuthService,
  ) {
    this.form = this.fb.group({
      cpf: [{value: '', disabled: this.isEditMode}, [Validators.required, this.verifyCpf()]],
      name: [{value: '', disabled: this.isEditMode}, [Validators.required, this.verifyName()]],
      email: [{value: '', disabled: this.isEditMode}, [Validators.required, this.verifyEmail()]],
      group: [{value: '', disabled: this.isEditMode}, [Validators.required]],
      accessLevel: [{value: '', disabled: this.isEditMode}, [Validators.required]],
      status: [true]
    });

  }

  ngOnInit() {
    const userId = this.route.snapshot.paramMap.get('id');
    if(userId){
      this.loadUserData(userId);
    }else{
      this.changeTitle('Novo Usuário');
    }
    this.loadAccessAccountGroups();
    this.loadAccessAccountProfiles();
  }

  hasPermission(permission: string): boolean{
    const hasPermission = this.authService.hasPermission(permission);
    return hasPermission;
  }

  loadUserData(userId: string) {
    const accessAccount = localStorage.getItem('accessAccount');
    if(accessAccount){
      this.userService.getUserAccessAccount(userId, accessAccount).subscribe({
        next: (response) => {
          if (response) {
            this.user = response
            this.isEditMode = true;
            this.selectedGroups = this.user.group.map(g => ({
              id: g.id,
              name: g.name,
              accessLevelName: g.permission.name,
              accessLevelId: g.permission.id
            }));
            this.form.patchValue({
              cpf: this.user.identification,
              name: this.user.name,
              email: this.user.email,
              group: this.user.group[0].id,
              accessLevel: this.user.group[0].permission.id,
              status: this.user.status === 'AC'
            });
            this.form.get('cpf')?.disable();
            this.form.get('name')?.disable();
            this.form.get('email')?.disable();
            if(this.hasPermission('CU003')){
              this.form.get('status')?.enable();
              this.form.get('group')?.enable();
              this.form.get('accessLevel')?.enable();
            }else{
              this.form.get('status')?.disable();
              this.form.get('group')?.disable();
              this.form.get('accessLevel')?.disable();
            }
          }
          this.changeTitle('Usuário - ' + this.user.name);
        },
        error: (error) => {
          console.log(error);
        }
      });
    }
  }

  save() {
    if (this.form.valid) {
      const cpf = this.form.get('cpf')?.value;
      const name = this.form.get('name')?.value;
      const email = this.form.get('email')?.value;
      const group = this.form.get('group')?.value;
      const accessLevel = this.form.get('accessLevel')?.value;

      if (this.isEditMode) {
        const userId = this.route.snapshot.paramMap.get('id')!;
        const status = this.form.get('status')?.value ? 'AC' : 'IN';
        const accessAccount = localStorage.getItem('accessAccount') || '';
        const groupId = localStorage.getItem('groupId') || '';
        const body: PutReqUserAccessAccount = {
          status: status,
          request: {
            accessAccount: accessAccount,
            group: groupId
          },
          groups: this.selectedGroups.map(g => ({
            group: g.id,
            profile: g.accessLevelId
          })),
        }
        console.log(body);
        this.user.status = status;
        this.userService.putUserAccessAccount(userId, accessAccount, body).subscribe({
          next: () => {
            const summary = this.translocoService.translate('user-detail-component.toast.update.success.title');
            this.messageService.add({ key: 'success', severity:'success', summary: summary, sticky: true});
            this.router.navigate(['/home/usuarios']);
          },
          error: (error) => {
            const summary = this.translocoService.translate('user-detail-component.toast.update.error.title');
            this.messageService.add({ key: 'error', severity:'error', summary: summary, sticky: true});
          }
        });
      } else {
        const accessAccount = localStorage.getItem('accessAccount') || '';
        const requester = localStorage.getItem('requester') || '';
        const groupId = localStorage.getItem('groupId') || '';
        
        const body: PostReqUser = {
          identification: cpf,
          name: name,
          email: email,
          userGroup: this.selectedGroups.map(g => ({
            group: g.id,
            profile: g.accessLevelId
          })),
          request: { accessAccount: accessAccount, group: groupId }
        }
        this.userService.postUser(body).subscribe({
          next: () => { 
            const summary = this.translocoService.translate('user-detail-component.toast.success.title');
            this.messageService.add({ key: 'success', severity:'success', summary: summary, sticky: true});
            this.router.navigate(['/home/usuarios']);
          },
          error: (error) => { 
            console.log(error.error);
            let summary;
            if (!error.__type){
              summary = this.translateErrorMessage(error.error);
            } else {
              summary = `user-detail-component.toast.error.${error.__type}`;
            }
            const translatedSummary = this.translocoService.translate(summary);
            this.messageService.add({ key: 'error', severity:'error', summary: translatedSummary, sticky: true});
          }
        });
       
      }
    }
  }

  translateErrorMessage(error: any): string {
    if (!error) return '';
    if (error.includes('UsernameExistsException')) {
      return 'user-detail-component.toast.error.UsernameExistsException';
    }
    const translatedMessage = `user-detail-component.toast.error.${error}`;
    return translatedMessage;
  }

  cancel() {
    if (this.form.touched) {
      const allFieldsEmpty = Object.values(this.form.controls).every(control => !control.value || control.value.trim() === '');
      if (allFieldsEmpty) {
        this.router.navigate(['/home/usuarios']);
      } else {
        const summary = this.translocoService.translate('user-detail-component.toast.cancel.title');
        const detail = this.translocoService.translate('user-detail-component.toast.cancel.description');
        this.messageService.add({ key: 'cancel', severity: 'custom', summary: summary, detail: detail, sticky: true });
      }
    } else {
      this.router.navigate(['/home/usuarios']);
    }
  }

  back(){
    this.router.navigate(['/home/usuarios']);
  }

  verifyCpf(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (!value) return null;

      const cleanValue = value.replace(/\D/g, '');
      
      if (cleanValue.length !== 11) {
        return { cpfLengthError: 'O CPF deve ter 11 caracteres numéricos.' };
      }

      // Verifica se todos os dígitos são iguais
      if (/^(\d)\1+$/.test(cleanValue)) {
        return { invalidDigits: 'CPF inválido.' };
      }

      // Validação do primeiro dígito verificador
      let sum = 0;
      for (let i = 0; i < 9; i++) {
        sum += parseInt(cleanValue.charAt(i)) * (10 - i);
      }
      let digit = 11 - (sum % 11);
      if (digit > 9) digit = 0;
      if (digit !== parseInt(cleanValue.charAt(9))) {
        return { invalidDigits: 'CPF inválido.' };
      }

      // Validação do segundo dígito verificador
      sum = 0;
      for (let i = 0; i < 10; i++) {
        sum += parseInt(cleanValue.charAt(i)) * (11 - i);
      }
      digit = 11 - (sum % 11);
      if (digit > 9) digit = 0;
      if (digit !== parseInt(cleanValue.charAt(10))) {
        return { invalidDigits: 'CPF inválido.' };
      }

      return null;
    };
  }

  verifyName(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const regex = /^[a-zA-Z\s]*$/;

      if (value && !regex.test(value)) {
        return { invalidUserName: 'O nome do usuário deve conter apenas letras.' };
      }

      if (value && value.length > 100) {
        return { maxLenghUserName: 'O nome do usuário deve ter no máximo 100 caracteres.' };
      }
      return null;
    };
  }

  verifyEmail(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      const EMAIL_FORMAT = /^[a-zA-Z0-9_.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+$/;
      if (value && !EMAIL_FORMAT.test(value)) {
        return { 'invalidEmailMask': true };
      }
      return null;
    }
  }

  loadAccessAccountGroups(){
    
    const accessAccount = localStorage.getItem('accessAccount');
    if(accessAccount){
      const params = {
        page: 1,
        size: 200
      }
      this.accessAccountService.postAccessAccountGroups(accessAccount, params).subscribe({
        next: (response) => {
          this.groups = response.data.map(group => ({
            label: group.name,
            value: group.id
          }));
        },
        error: (error) => {
          console.log(error);
        }
      });
    }
  }
  
  loadAccessAccountProfiles(){
    this.accessAccountService.getAccessAccountProfilesList().subscribe({
      next: (response) => {
        this.profiles = response.map(profile => ({
          label: profile.name,
          value: profile.id
        }));
      },
      error: (error) => {
        console.log(error);
      }
    });
  }
  
  changeTitle(value:string){
    this.titleService.setTitle(`Total Cash - ${value}`);
  }

  removeGroup(index: number){
    this.selectedGroups.splice(index, 1);
  }

  addGroup(){
    if (this.form.get('group')?.valid && this.form.get('accessLevel')?.valid) {
      const group = this.form.get('group')?.value;
      const accessLevel = this.form.get('accessLevel')?.value;
      const groupExists = this.selectedGroups.some(selectedGroup => selectedGroup.value === group.label);
      if (!groupExists) {
        this.selectedGroups.push({
          id: group.value,
          name: group.label,
          accessLevelName: accessLevel.label,
          accessLevelId: accessLevel.value
        });
      } else {
        this.messageService.add({
          key: 'error',
          severity: 'error',
          summary: this.translocoService.translate('group-detail-component.error.error'),
          detail: this.translocoService.translate('group-detail-component.error.userAlreadyExists')
        });
      }
      this.form.get('group')?.reset();
      this.form.get('accessLevel')?.reset();
      this.form.get('group')?.clearValidators();
      this.form.get('group')?.updateValueAndValidity();
      this.form.get('accessLevel')?.clearValidators();
      this.form.get('accessLevel')?.updateValueAndValidity();
    }
  }
}
